import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  UpdateContentSourceFieldInput,
  getPropertyContentSources,
  updatePropertyContentSources,
} from '~/services/AccommodationService';

export const propertyContentSourcesQueryKeys = {
  all: ['propertyContentSources'] as const,
  details: () => [...propertyContentSourcesQueryKeys.all, 'detail'] as const,
  detail: (propertyId: string) => [...propertyContentSourcesQueryKeys.details(), propertyId] as const,
};

interface UseGetPropertyContentSourcesParams {
  propertyId: string;
  enabled?: boolean;
}

export function useGetPropertyContentSources({ propertyId, enabled = true }: UseGetPropertyContentSourcesParams) {
  return useQuery({
    queryKey: propertyContentSourcesQueryKeys.detail(propertyId),
    queryFn: async ({ signal }) => {
      const response = await getPropertyContentSources({ propertyId, signal });
      return response.result;
    },
    enabled: enabled && !!propertyId,
  });
}

interface UseUpdatePropertyContentSourcesParams {
  propertyId: string;
  fields: UpdateContentSourceFieldInput[];
}

export function useUpdatePropertyContentSources() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async ({ propertyId, fields }: UseUpdatePropertyContentSourcesParams) => {
      const response = await updatePropertyContentSources({ propertyId, fields });
      return response.result;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: propertyContentSourcesQueryKeys.detail(variables.propertyId),
      });
      enqueueSnackbar('Custom content updated', { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to update custom content: ${error.message}`, { variant: 'error' });
    },
  });
}
